export class ApiClientError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public payload?: unknown,
    public response?: Response,
    public retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export type ApiValidationErrors = Record<string, string>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The API returns bean-validation failures as `{ field: message }`, including
 * nested batch paths such as `items[3].expiryDate`.
 */
export function getApiValidationErrors(error: unknown): ApiValidationErrors {
  if (!(error instanceof ApiClientError) || !isRecord(error.payload)) return {};

  return getApiValidationErrorsFromPayload(error.payload);
}

type ApiBody = BodyInit | object | null | undefined;

type ApiRequestOptions = Omit<RequestInit, "body"> & {
  body?: ApiBody;
};

// Bun replaces literal environment references in browser bundles.
function publicApiBase(): string {
  try {
    return process.env.BUN_PUBLIC_API_BASE ?? "";
  } catch {
    return "";
  }
}

function publicCsrfCookieName(): string {
  try {
    return process.env.BUN_PUBLIC_CSRF_COOKIE_NAME ?? "csrf_token";
  } catch {
    return "csrf_token";
  }
}

const API_BASE_URL = publicApiBase().replace(/\/$/, "");
const CSRF_COOKIE_NAME = publicCsrfCookieName();
const CSRF_HEADER_NAME = "X-CSRF-Token";
const CSRF_ENDPOINT = "auth/csrf";

let csrfToken: string | null = null;
let csrfTokenRequest: Promise<string> | null = null;

function cleanEndpoint(endpoint: string): string {
  return endpoint.replace(/^\/+/, "").replace(/^(api)\//, "");
}

function buildUrl(endpoint: string): string {
  const path = cleanEndpoint(endpoint);
  return `${API_BASE_URL}/api/${path}`;
}

function isUnsafeMethod(method?: string): boolean {
  return ["POST", "PUT", "PATCH", "DELETE"].includes((method ?? "GET").toUpperCase());
}

function shouldAttachCsrf(endpoint: string, method?: string): boolean {
  return isUnsafeMethod(method) && cleanEndpoint(endpoint) !== CSRF_ENDPOINT;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;

  const encodedName = `${encodeURIComponent(name)}=`;
  const cookie = document.cookie.split("; ").find((entry) => entry.startsWith(encodedName));
  return cookie ? decodeURIComponent(cookie.slice(encodedName.length)) : null;
}

async function fetchCsrfToken(): Promise<string> {
  const response = await fetch(buildUrl(CSRF_ENDPOINT), {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "include",
    cache: "no-store",
  });
  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    throw new ApiClientError(response.status, resolveErrorMessage(response.status, payload), payload, response);
  }

  const tokenFromBody = isRecord(payload) && typeof payload.token === "string" ? payload.token : null;
  const token = tokenFromBody ?? readCookie(CSRF_COOKIE_NAME);
  if (!token) {
    throw new Error("The API did not return a CSRF token.");
  }

  return token;
}

async function ensureCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  csrfTokenRequest ??= fetchCsrfToken().finally(() => {
    csrfTokenRequest = null;
  });
  csrfToken = await csrfTokenRequest;
  return csrfToken;
}

export function invalidateCsrfToken(): void {
  csrfToken = null;
}

function isCsrfFailure(statusCode: number, payload: unknown): boolean {
  return statusCode === 403 && isRecord(payload) && payload.code === "CSRF_INVALID";
}

function parseRetryAfter(response: Response): number | undefined {
  const value = response.headers.get("Retry-After");
  if (!value) return undefined;

  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds));

  const date = Date.parse(value);
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, Math.ceil((date - Date.now()) / 1000));
}

function isBodyInit(value: unknown): value is BodyInit {
  return (
    typeof value === "string" ||
    value instanceof FormData ||
    value instanceof URLSearchParams ||
    value instanceof Blob ||
    value instanceof ArrayBuffer ||
    ArrayBuffer.isView(value)
  );
}

function resolveBody(body: ApiBody): BodyInit | undefined {
  if (body === undefined || body === null) return undefined;
  if (isBodyInit(body)) return body;
  return JSON.stringify(body);
}

function buildHeaders(headers?: HeadersInit, body?: ApiBody): Headers {
  const mergedHeaders = new Headers({
    Accept: "application/json",
  });

  const resolvedBody = resolveBody(body);
  if (resolvedBody !== undefined && !(resolvedBody instanceof FormData)) {
    mergedHeaders.set("Content-Type", "application/json");
  }

  if (headers) {
    const customHeaders = new Headers(headers);
    customHeaders.forEach((value, key) => mergedHeaders.set(key, value));
  }

  return mergedHeaders;
}

async function parseResponsePayload(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    if (response.ok) {
      throw new ApiClientError(
        response.status,
        "Réponse API invalide. Vérifiez l’adresse du serveur API.",
        null,
        response,
      );
    }
    return text;
  }
}

function resolveErrorMessage(statusCode: number, payload: unknown): string {
  if (isRecord(payload)) {
    const maybeMessage = payload.message ?? payload.error;
    if (typeof maybeMessage === "string" && maybeMessage.trim()) return maybeMessage;

    const validationMessages = Object.values(getApiValidationErrorsFromPayload(payload));
    if (validationMessages.length > 0) return [...new Set(validationMessages)].join(" ");
  }

  if (typeof payload === "string" && payload.trim().length > 0) {
    return payload;
  }

  return `Request failed with status ${statusCode}`;
}

function getApiValidationErrorsFromPayload(payload: Record<string, unknown>): ApiValidationErrors {
  return Object.entries(payload).reduce<ApiValidationErrors>((errors, [field, message]) => {
    if (field !== "message" && field !== "error" && typeof message === "string" && message.trim()) {
      errors[field] = message;
    }
    return errors;
  }, {});
}

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler | null = null;

/**
 * Lets the auth provider drop the session on a 401. The client never navigates
 * on its own: a hard redirect here would reload the app mid-request, including
 * during sign-in, and look like a failed login.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

async function requestRaw(endpoint: string, options: ApiRequestOptions = {}): Promise<Response> {
  const { body, headers, ...rest } = options;
  const method = rest.method?.toString().toUpperCase() ?? "GET";
  const requestHeaders = new Headers(headers);

  if (shouldAttachCsrf(endpoint, method)) {
    requestHeaders.set(CSRF_HEADER_NAME, await ensureCsrfToken());
  }

  const response = await fetch(buildUrl(endpoint), {
    ...rest,
    headers: buildHeaders(requestHeaders, body),
    body: resolveBody(body),
    credentials: options.credentials ?? "include",
  });

  if (response.status === 401) {
    onUnauthorized?.();
  }

  return response;
}

async function requestJson<T>(endpoint: string, options: ApiRequestOptions = {}, csrfRetry = false): Promise<T> {
  const response = await requestRaw(endpoint, options);
  const payload = await parseResponsePayload(response);

  if (
    !response.ok &&
    !csrfRetry &&
    shouldAttachCsrf(endpoint, options.method?.toString()) &&
    isCsrfFailure(response.status, payload)
  ) {
    invalidateCsrfToken();
    return requestJson<T>(endpoint, options, true);
  }

  if (!response.ok) {
    throw new ApiClientError(
      response.status,
      resolveErrorMessage(response.status, payload),
      payload,
      response,
      parseRetryAfter(response),
    );
  }

  return payload as T;
}

export const apiClient = {
  requestRaw,
  requestJson,
  get: <T>(endpoint: string, options: Omit<ApiRequestOptions, "method" | "body"> = {}) =>
    requestJson<T>(endpoint, { ...options, method: "GET" }),
  post: <T>(endpoint: string, body?: ApiBody, options: Omit<ApiRequestOptions, "method" | "body"> = {}) =>
    requestJson<T>(endpoint, { ...options, method: "POST", body }),
  put: <T>(endpoint: string, body?: ApiBody, options: Omit<ApiRequestOptions, "method" | "body"> = {}) =>
    requestJson<T>(endpoint, { ...options, method: "PUT", body }),
  patch: <T>(endpoint: string, body?: ApiBody, options: Omit<ApiRequestOptions, "method" | "body"> = {}) =>
    requestJson<T>(endpoint, { ...options, method: "PATCH", body }),
  delete: <T>(endpoint: string, options: Omit<ApiRequestOptions, "method" | "body"> = {}) =>
    requestJson<T>(endpoint, { ...options, method: "DELETE" }),
};
